import React, { useCallback, useRef, useState } from 'react';
import { Button, Upload, message } from 'antd';
import { FolderOpenOutlined, UploadOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import PSAIModal from '@PSAIComponents/PSAIModal';
import Utils from '@utils/utils';
import useResizeObserver from '@utils/useResizeObserver';
import PickFromProject from '../PickFromProject';
import Iconfont from '@utils/Iconfont';

const CARD_LAYOUT = {
    1: { width: 474, height: 226, gap: 0 },
    2: { width: 300, height: 226, gap: 24 },
    3: { width: 210, height: 226, gap: 8 },
};

const getPlaceholderCards = (cards = [], uploadExamples = []) => {
    if (cards?.length) return cards.slice(0, 3);

    const count = Math.min(Math.max(uploadExamples?.length || 1, 1), 3);
    return Array.from({ length: count }).map((_, index) => ({
        key: `placeholder-${index}`,
    }));
};

const WaitUploadPage = (props) => {
    const [uploading, setUploading] = useState(false);
    const [open, setOpen] = useState(false);
    const [wrapWidth, setWrapWidth] = useState();
    const projectListRef = useRef();
    const fileInputRef = useRef();

    const placeholderCards = getPlaceholderCards(props.placeholderCards, props.uploadExamples);
    const cardCount = placeholderCards.length || 1;
    const cardLayout = CARD_LAYOUT[cardCount] || CARD_LAYOUT[1];

    const onPickImage = (e) => {
        e?.stopPropagation?.();
        const status = Utils.checkLoginStatus(() => onPickImage());
        if (!status) return;
        setOpen(true);
    };

    const uploadDebounce = useCallback(_.debounce(async (file, fileList) => {
        if (props.needLogin) {
            const status = Utils.checkLoginStatus(() => uploadDebounce(file, fileList));
            if (!status) return;
        }

        setUploading(true);
        props.multiple ?
            await props.onUpload(fileList) :
            await props.onUpload(file);
        setUploading(false);
    }, 100), []);

    const beforeUpload = async (file, fileList) => {
        uploadDebounce(file, fileList);
        return false;
    };

    const handleLocalUploadClick = (e) => {
        e?.stopPropagation?.();
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const fileList = Array.from(e.target.files || []);
        if (!fileList.length) return;
        await beforeUpload(fileList[0], fileList);
        e.target.value = '';
    };

    const handleDrop = async (e) => {
        if ('getFilesAndDirectories' in e.dataTransfer) {
            const filesAndDirs = await e.dataTransfer.getFilesAndDirectories();
            const fileList = await Utils.getFilesFromDirs(filesAndDirs, '/');
            uploadDebounce(fileList[0], fileList);
            return false;
        }
    };

    const handleOk = async () => {
        const url = await projectListRef.current.onSubmit();
        const isEmpty = Array.isArray(url) ? url.length == 0 : !url;
        if (isEmpty) return message.info(props.multiple ? '请至少选择一张图片' : '请选择一张图片');
        if (props.onValidate) {
            if (Array.isArray(url)) {
                const validateList = await Promise.all(url.map(item => props.onValidate(item)));
                if (validateList.some(item => !item)) return false;
            } else {
                const validate = await props.onValidate(url);
                if (!validate) return false;
            }
        }
        setOpen(false);
        props.onChange(url);
    };

    const onDoubleClick = async (url) => {
        if (props.multiple) return;
        if (props.onValidate) {
            const validate = await props.onValidate(url);
            if (!validate) return false;
        }
        setOpen(false);
        props.onChange(url);
    };

    const targetRef = useResizeObserver((entries) => {
        for (let entry of entries) {
            setWrapWidth(entry.contentRect.width);
        }
    });

    const tips = Array.isArray(props.uploadTips)
        ? props.uploadTips.filter(Boolean)
        : String(props.uploadTips || '')
            .split(/[\r\n]+/)
            .map(item => item.trim())
            .filter(Boolean);

    return (
        <>
            <div className={`${styles.Container} ${wrapWidth < 778 ? styles.Tiny : ''}`} ref={targetRef}>
                <div id="tour_target_upload" className={styles.mainPart}>
                    <Upload.Dragger
                        accept="image/jpeg,image/png,image/webp,image/avif"
                        listType="picture-card"
                        beforeUpload={beforeUpload}
                        className={styles.uploader}
                        showUploadList={false}
                        openFileDialogOnClick={false}
                        multiple={props.multiple}
                        maxCount={props.maxCount || 1}
                        onDrop={handleDrop}
                    >
                        <div className={styles.panel}>
                            <input
                                ref={fileInputRef}
                                className={styles.hiddenInput}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/avif"
                                multiple={props.multiple}
                                onChange={handleFileChange}
                            />
                            {props.title && <h2 className={styles.title}>{props.title}</h2>}
                            <div className={styles.actions}>
                                {props.pickFromProject && (
                                    <Button
                                        className={`${styles.actionButton} ${styles.projectButton}`}
                                        onClick={onPickImage}
                                    >
                                        <Iconfont type="icon-Material-FolderOutlined" />
                                        <span>从 [我的项目] 中选择</span>
                                    </Button>
                                )}
                                <Button
                                    type="primary"
                                    className={`${styles.actionButton} ${styles.uploadButton}`}
                                    loading={uploading}
                                    onClick={handleLocalUploadClick}
                                >
                                    {(props.showBatchTag || props.multiple) && <span className={styles.batchTag}>批量</span>}
                                    <UploadOutlined />
                                    <span>本地上传</span>
                                </Button>
                            </div>
                            {!!tips.length && (
                                <div className={styles.tips}>
                                    {tips.map((item, index) => (
                                        <p key={`${item}-${index}`}>{item}</p>
                                    ))}
                                </div>
                            )}
                            {props.showUpImg ? (
                                <div className={styles.previewWrap}>
                                    {props.imageSize && <span className={styles.previewSize}>{props.imageSize}</span>}
                                    <img className={styles.previewImage} src={props.showUpImg} alt="" />
                                </div>
                            ) : (
                                <div
                                    className={styles.cardList}
                                    style={{
                                        '--card-gap': `${cardLayout.gap}px`,
                                    }}
                                >
                                    {placeholderCards.map((card, index) => (
                                        <div
                                            key={card.key || `${card.title || 'card'}-${index}`}
                                            className={styles.cardItem}
                                            style={{
                                                '--card-width': `${cardLayout.width}px`,
                                                '--card-height': `${cardLayout.height}px`,
                                            }}
                                        >
                                            <div className={`${styles.cardVisual} ${styles[`cardVisual${Math.min(index + 1, 3)}`]}`}>
                                                {card.image ? (
                                                    <img className={styles.cardImage} src={card.image} alt="" />
                                                ) : (
                                                    <>
                                                        <span className={`${styles.layer} ${styles.layerBack}`}></span>
                                                        <span className={`${styles.layer} ${styles.layerMiddle}`}></span>
                                                        <span className={`${styles.layer} ${styles.layerFront}`}></span>
                                                    </>
                                                )}
                                            </div>
                                            {(card.title || card.subtitle) && (
                                                <div className={styles.cardText}>
                                                    {card.title && <p className={styles.cardTitle}>{card.title}</p>}
                                                    {card.subtitle && <p className={styles.cardSubtitle}>{card.subtitle}</p>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Upload.Dragger>
                </div>
                {props.children && <div className={styles.rightPart}>{props.children}</div>}
                <PSAIModal
                    open={open}
                    maxWidth={980}
                    title="选择图片"
                    onOk={handleOk}
                    onCancel={() => setOpen(false)}
                >
                    <PickFromProject
                        ref={projectListRef}
                        onDoubleClick={onDoubleClick}
                        useImageType={props.useImageType}
                        showAllType={props.showAllType}
                        multiple={props.multiple}
                        maxCount={props.maxCount}
                    />
                </PSAIModal>
            </div>
        </>
    );
};

export default WaitUploadPage;

